import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Calendar } from 'lucide-react';
import { Announcement } from '../../types/database';

interface AnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  announcements: Announcement[];
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({ 
  isOpen, 
  onClose, 
  announcements 
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6 border border-purple-500/30 shadow-2xl"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-white font-rajdhani font-bold text-2xl flex items-center gap-2">
                <Bell className="w-6 h-6 text-purple-400" />
                Announcements
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-white transition">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {announcements.length === 0 ? (
              <div className="text-center py-8">
                <Bell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No announcements yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {announcements.map((announcement, idx) => (
                  <motion.div
                    key={announcement.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`p-4 rounded-xl border ${
                      announcement.type === 'event' ? 'bg-purple-500/10 border-purple-500/30' :
                      announcement.type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                      announcement.type === 'success' ? 'bg-green-500/10 border-green-500/30' :
                      'bg-blue-500/10 border-blue-500/30'
                    }`}
                  >
                    <h3 className="text-white font-rajdhani font-bold mb-1">{announcement.title}</h3>
                    <p className="text-gray-300 text-sm mb-2">{announcement.message}</p>
                    <p className="text-gray-500 text-xs flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {announcement.date}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AnnouncementModal;